import React  from "react";
import { useTranslation } from "react-i18next";
import {useFormContext, Controller} from "react-hook-form";
import cx from "classnames";

import TextField from "@mui/material/TextField";
import {FormControl, FormControlLabel, InputAdornment, Radio, RadioGroup} from "@mui/material";
import Typography from "@mui/material/Typography";

import { PACKAGE_FORM, PackageForm } from '../add_form/helpers';

import {
  checkDecimal,
  checkDecimalsAfterDot,
} from "../../../../../../../../../../../../../utils/validation";
import {ADORNMENTS, COLORS} from "../../../../../../../../../../../../../config/constants";
import { useLazyChangePriceQuery, useLazyChangePlacesQuery } from "../../../../../../../../../../../../../api/calc";
import {useFormattedDecimalInput} from "../../../../../../../../../../../../../utils/hooks";
import {useDemoCommonProductFields} from "components/cabinet/demo/steps/packaging/use-product-form";

const enum PRICE_SWITCH_TYPE {
  UNIT_PRICE = 'unit_price',
  PRICE = 'price',
}

const PRICE_DECIMALS = 2;
const UNIT_PRICE_DECIMALS = 3;

type Props = {
  isDemoRecalculating?: boolean
  onPriceModeChange?(value: string): void
}

const CommonFields = ({ isDemoRecalculating = false, onPriceModeChange }: Props) => {

  const { t } = useTranslation()

  const { register, formState, getValues, setValue, setError, clearErrors, control } = useFormContext<PackageForm>()

  const { errors, dirtyFields } = formState;

  const demoPlayback = useDemoCommonProductFields(isDemoRecalculating)
  const isDemoMode = demoPlayback.isDemoMode

  const [ currentSwitch, setCurrentSwitch ] = React.useState<string>(
    PRICE_SWITCH_TYPE.UNIT_PRICE
  );
  React.useEffect(() => { onPriceModeChange?.(currentSwitch); }, [ currentSwitch, onPriceModeChange ]);
  const setIsUpdatingCalculatedFields = (value: boolean) => setValue(PACKAGE_FORM.IS_UPDATING_CALCULATED_FIELDS, value);

  const [ changePriceRequest ] = useLazyChangePriceQuery()
  const [ changePlacesRequest ] = useLazyChangePlacesQuery()

  const decimalInput = useFormattedDecimalInput();

  const isUnitPriceChanged = dirtyFields[PACKAGE_FORM.UNIT_PRICE];
  const isPriceChanged = dirtyFields[PACKAGE_FORM.PRICE];

  const errorDecimal = t('errors__message__onlyDecimals');

  const errorThreeDecimalsAfterDot = t('errors__message__incorrectValueThreeDecimalsAfterComma');

  const errorTwoDecimalsAfterDot = t('errors__message__incorrectValueTwoDecimalsAfterComma');

  const checkErrors = (isSuccess: boolean, field: any, errors?: any) => {

    if (isSuccess) {
      clearErrors(field)
    } else {
      errors[field]?.[0] ? setError(field, { type: 'custom', message: errors[field]?.[0]} ) : clearErrors(field)
    }

    const pieces_in_cartoon = getValues()[PACKAGE_FORM.PIECES_IN_CARTOON] // количество в 1 упаковке
    const pieces = getValues()[PACKAGE_FORM.PIECES] // общее количество

    if (pieces < pieces_in_cartoon) {
      setError(PACKAGE_FORM.PIECES, { type: 'custom', message: t('errors__message__incorrectValuePieces')});
    } else {
      clearErrors(PACKAGE_FORM.PIECES);
    }

  }

  const setCalculatedDecimalValue = (
    name: PACKAGE_FORM.PRICE | PACKAGE_FORM.UNIT_PRICE,
    value: string | number | undefined
  ) => {
    if (value === undefined || value === '') return;

    const rawValue = typeof value === 'string' ? Number(value.replace(',', '.')) : value;

    if (!Number.isFinite(rawValue)) return;

    setValue(name, rawValue, { shouldValidate: true });
  }

  const requestChangePrice = async (field: PACKAGE_FORM.UNIT_PRICE | PACKAGE_FORM.PRICE | PACKAGE_FORM.PIECES | PACKAGE_FORM.PLACES_MANUAL) => {

    const pieces = getValues()[PACKAGE_FORM.PIECES]
    const price = getValues()[PACKAGE_FORM.PRICE]
    const unit_price = getValues()[PACKAGE_FORM.UNIT_PRICE]

    if (field === PACKAGE_FORM.UNIT_PRICE && pieces){

      // @ts-ignore
      if (unit_price === '0') {
        setError(PACKAGE_FORM.UNIT_PRICE, { type: 'custom', message: t('errors__message__incorrectValue') });
        return;
      } else {
        clearErrors(PACKAGE_FORM.UNIT_PRICE);
        clearErrors(PACKAGE_FORM.PRICE);
      }

      await changePriceRequest({pieces, unit_price}).then((data) => {
        // @ts-ignore
        const total = data?.data?.[PACKAGE_FORM.PRICE_TOTAL];

        setCalculatedDecimalValue(PACKAGE_FORM.PRICE, total);

        // @ts-ignore
        const errorData = data?.error?.data;

        checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES, errorData)
        checkErrors(Boolean(data?.data), PACKAGE_FORM.UNIT_PRICE, errorData)
      });
    }

    if (field === PACKAGE_FORM.PRICE && pieces){

      // @ts-ignore
      if (price === '0') {
        setError(PACKAGE_FORM.PRICE, { type: 'custom', message: t('errors__message__incorrectValue') });
        return;
      } else {
        clearErrors(PACKAGE_FORM.PRICE);
        clearErrors(PACKAGE_FORM.UNIT_PRICE);
      }

      await changePriceRequest({price, pieces}).then((data) => {
        if (data?.data){
          const unitPriceTotal = data?.data?.[PACKAGE_FORM.UNIT_PRICE_TOTAL];

          setCalculatedDecimalValue(PACKAGE_FORM.UNIT_PRICE, unitPriceTotal);
        }

        // @ts-ignore
        const errorData = data?.error?.data;

        checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES, errorData)
        checkErrors(Boolean(data?.data), PACKAGE_FORM.PRICE, errorData)
      });
    }

    if (field === PACKAGE_FORM.PIECES) {

      if (unit_price && currentSwitch === PRICE_SWITCH_TYPE.UNIT_PRICE) {

        await changePriceRequest({unit_price, pieces}).then((data) => {

          if (data?.data){
            const priceTotal = data.data[PACKAGE_FORM.PRICE_TOTAL];

            setCalculatedDecimalValue(PACKAGE_FORM.PRICE, priceTotal);
          }

          // @ts-ignore
          const errorData = data?.error?.data;
          checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES, errorData)
        });
      } else if (price && currentSwitch === PRICE_SWITCH_TYPE.PRICE) {

        await changePriceRequest({price, pieces}).then((data) => {

          if (data?.data){
            const unitPriceTotal = data.data[PACKAGE_FORM.UNIT_PRICE_TOTAL];

            setCalculatedDecimalValue(PACKAGE_FORM.UNIT_PRICE, unitPriceTotal);
          }

          // @ts-ignore
          const errorData = data?.error?.data;

          checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES, errorData)
          checkErrors(Boolean(data?.data), PACKAGE_FORM.PRICE, errorData)
        });
      }

      if (!unit_price && currentSwitch === PRICE_SWITCH_TYPE.UNIT_PRICE && isUnitPriceChanged) {
        setError(PACKAGE_FORM.UNIT_PRICE, { type: 'custom', message: t('common__text__isRequired') });
      }

      if (!price && currentSwitch === PRICE_SWITCH_TYPE.PRICE && isPriceChanged) {
        setError(PACKAGE_FORM.PRICE, { type: 'custom', message: t('common__text__isRequired') });
      }
    }
  }

  const requestChangePlaces = async (field: PACKAGE_FORM.PIECES_IN_CARTOON | PACKAGE_FORM.PLACES_MANUAL | PACKAGE_FORM.PIECES) => {
    const pieces_in_cartoon = getValues()[PACKAGE_FORM.PIECES_IN_CARTOON] // количество в 1 упаковке
    const places = getValues()[PACKAGE_FORM.PLACES_MANUAL] // количество упаковок
    const pieces = getValues()[PACKAGE_FORM.PIECES] // общее количество

    if (field === PACKAGE_FORM.PIECES_IN_CARTOON && places){

      if (pieces) {
        await changePlacesRequest({pieces_in_cartoon, pieces}).then((data) => {

          if (data?.data){
            const placesTotal = data.data['places_total'];
            setValue(PACKAGE_FORM.PLACES_MANUAL, parseFloat(Number(placesTotal).toFixed(2)));
          }

          // @ts-ignore
          const errorData = data?.error?.data;

          checkErrors(Boolean(data?.data), PACKAGE_FORM.PLACES_MANUAL, errorData)
          checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES, errorData)

          return requestChangePrice(PACKAGE_FORM.PIECES)

        });
      } else {
        await changePlacesRequest({pieces_in_cartoon, places}).then((data) => {

          if (data?.data){
            const piecesTotal = data.data['pieces_total'];
            setValue(PACKAGE_FORM.PIECES, parseFloat(Number(piecesTotal).toFixed(2)));
          }

          // @ts-ignore
          const errorData = data?.error?.data;

          checkErrors(Boolean(data?.data), PACKAGE_FORM.PLACES_MANUAL, errorData)
          checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES, errorData)

          return requestChangePrice(PACKAGE_FORM.PIECES)

        });
      }

    }

    else if (field === PACKAGE_FORM.PLACES_MANUAL && pieces_in_cartoon){

      if (pieces) {
        await changePlacesRequest({pieces, places}).then((data) => {

          if (data?.data){
            const piecesInCartoonTotal = data.data['pieces_in_cartoon_total'];
            setValue(PACKAGE_FORM.PIECES_IN_CARTOON, parseFloat(Number(piecesInCartoonTotal).toFixed(2)));
          }

          // @ts-ignore
          const errorData = data?.error?.data;

          checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES_IN_CARTOON, errorData)
          checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES, errorData)

          return requestChangePrice(PACKAGE_FORM.PIECES)

        });
      } else {
        await changePlacesRequest({pieces_in_cartoon, places}).then((data) => {

          if (data?.data){
            const pieces = data.data['pieces_total'];
            setValue(PACKAGE_FORM.PIECES, parseFloat(Number(pieces).toFixed(2)));
          }

          // @ts-ignore
          const errorData = data?.error?.data;

          checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES_IN_CARTOON, errorData)
          checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES, errorData)

          return requestChangePrice(PACKAGE_FORM.PIECES)

        });
      }

    }

    else if (field === PACKAGE_FORM.PIECES_IN_CARTOON && pieces || field === PACKAGE_FORM.PIECES && pieces_in_cartoon){

      await changePlacesRequest({pieces_in_cartoon, pieces}).then((data) => {

        if (data?.data) {
          const placesTotal = data.data['places_total'];
          setValue(PACKAGE_FORM.PLACES_MANUAL, parseFloat(Number(placesTotal).toFixed(2)));
        }

        // @ts-ignore
        const errorData = data?.error?.data;

        checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES_IN_CARTOON, errorData)
        checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES, errorData)

        return requestChangePrice(PACKAGE_FORM.PIECES)

      });
    }

    else if (field === PACKAGE_FORM.PLACES_MANUAL && pieces || field === PACKAGE_FORM.PIECES && places){

      await changePlacesRequest({places, pieces}).then((data) => {

        if (data?.data){
          const piecesInCartoonTotal = data.data['pieces_in_cartoon_total'];
          setValue(PACKAGE_FORM.PIECES_IN_CARTOON, parseFloat(Number(piecesInCartoonTotal).toFixed(2)));
        }

        // @ts-ignore
        const errorData = data?.error?.data;

        checkErrors(Boolean(data?.data), PACKAGE_FORM.PLACES_MANUAL, errorData)
        checkErrors(Boolean(data?.data), PACKAGE_FORM.PIECES, errorData)

        return requestChangePrice(PACKAGE_FORM.PIECES)

      });
    }

  }

  const handleChangePrice = (field: PACKAGE_FORM.UNIT_PRICE | PACKAGE_FORM.PRICE | PACKAGE_FORM.PIECES | PACKAGE_FORM.PLACES_MANUAL) => {
    setIsUpdatingCalculatedFields(true);
    requestChangePrice(field).finally(() => setIsUpdatingCalculatedFields(false));
  }

  const handleChangePlaces = (field: PACKAGE_FORM.PIECES_IN_CARTOON | PACKAGE_FORM.PLACES_MANUAL | PACKAGE_FORM.PIECES) => {
    setIsUpdatingCalculatedFields(true);
    requestChangePlaces(field).finally(() => setIsUpdatingCalculatedFields(false));
  }

  const handleChangeRadioButton = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isDemoMode) {
      return
    }

    // @ts-ignore
    setCurrentSwitch((event.target as HTMLInputElement).value);
  };

  const isUnitPriceInactive = currentSwitch !== PRICE_SWITCH_TYPE.UNIT_PRICE;
  const isTotalPriceInactive = currentSwitch !== PRICE_SWITCH_TYPE.PRICE;
  const hasUnitPriceError = Boolean(errors[PACKAGE_FORM.UNIT_PRICE]);
  const hasTotalPriceError = Boolean(errors[PACKAGE_FORM.PRICE]);
  const shouldStyleUnitPriceInactive = isUnitPriceInactive && !hasUnitPriceError;
  const shouldStyleTotalPriceInactive = isTotalPriceInactive && !hasTotalPriceError;

  const inactiveStyle = {
    '.MuiOutlinedInput-notchedOutline': {
      borderStyle: 'dotted',
    },
    '.MuiOutlinedInput-input': {
      color: COLORS.LIGHT_TEXT_DISABLED,
    },
    '.MuiInputLabel-root': {
      color: COLORS.LIGHT_TEXT_DISABLED,
    }
  };

  const priceOptionSx = {
    display: 'grid',
    gridTemplateColumns: '40px minmax(0, 1fr)',
    rowGap: '16px',
    alignItems: 'start',
    margin: 0,
    '.MuiRadio-root': {
      padding: '8px',
    },
  };

  const priceCaptionSx = {
    display: 'flex',
    alignItems: 'center',
    minHeight: '40px',
    gridColumn: 2,
    color: COLORS.BLACK,
  };

  return <>
    <div className='add-product__common'>
      <TextField
        className={`add-product__${PACKAGE_FORM.PIECES}`}
        {...register(PACKAGE_FORM.PIECES, { required: true, valueAsNumber: true, validate: value => checkDecimal(value, errorDecimal),  min: { value: 1, message: t('errors__message__incorrectValue')} })}
        error={Boolean(errors[PACKAGE_FORM.PIECES])}
        helperText={errors[PACKAGE_FORM.PIECES]?.message}
        variant='outlined'
        label={t('common__text__totalQuantity')}
        InputLabelProps={{ shrink: true }}
        // disabled={isLoading}
        InputProps={{
          endAdornment: <InputAdornment position='end'>{t('units__text__items')}</InputAdornment>
        }}
        inputProps={demoPlayback.getEditableInputProps(PACKAGE_FORM.PIECES)}
        onBlur={() => handleChangePlaces(PACKAGE_FORM.PIECES)}
      />
      <TextField
        className={`add-product__${PACKAGE_FORM.PLACES_MANUAL}`}
        {...register(PACKAGE_FORM.PLACES_MANUAL, { required: true, valueAsNumber: true, validate: value => checkDecimal(value, errorDecimal),  min: { value: 1, message: t('errors__message__incorrectValue')} })}
        error={Boolean(errors[PACKAGE_FORM.PLACES_MANUAL])}
        helperText={errors[PACKAGE_FORM.PLACES_MANUAL]?.message}
        variant='outlined'
        label={t('common__text__numberOfPlaces')}
        InputLabelProps={{ shrink: true }}
        disabled={demoPlayback.disabled}
        InputProps={{
          endAdornment: <InputAdornment position='end'>{t('units__text__items')}</InputAdornment>
        }}
        onBlur={() => handleChangePlaces(PACKAGE_FORM.PLACES_MANUAL)}
      />
      <TextField
        className={`add-product__${PACKAGE_FORM.PIECES_IN_CARTOON}`}
        {...register(PACKAGE_FORM.PIECES_IN_CARTOON, { required: true, valueAsNumber: true, validate: value => checkDecimal(value, errorDecimal), min: { value: 1, message: t('errors__message__incorrectValue')} })}
        error={Boolean(errors[PACKAGE_FORM.PIECES_IN_CARTOON])}
        helperText={errors[PACKAGE_FORM.PIECES_IN_CARTOON]?.message}
        variant='outlined'
        label={t('common__text__quantityPerPackage')}
        InputLabelProps={{ shrink: true }}
        disabled={demoPlayback.disabled}
        InputProps={{
          endAdornment: <InputAdornment position='end'>{t('units__text__items')}</InputAdornment>
        }}
        onBlur={() => handleChangePlaces(PACKAGE_FORM.PIECES_IN_CARTOON)}
      />
    </div>
    <div className='add-product__switch'>
      <FormControl sx={{
        width: '100%',
        '.MuiFormGroup-root' : {
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '24px'
        }
      }}>
        <RadioGroup
          value={currentSwitch}
          onChange={handleChangeRadioButton}

        >
          <FormControlLabel
            value={PRICE_SWITCH_TYPE.UNIT_PRICE}
            control={
              <Radio
                checked={currentSwitch === PRICE_SWITCH_TYPE.UNIT_PRICE}
                value={PRICE_SWITCH_TYPE.UNIT_PRICE}
                disabled={demoPlayback.disabled}
              />
            }
            onClick={() => {
              if (isDemoMode) {
                return
              }

              setCurrentSwitch(PRICE_SWITCH_TYPE.UNIT_PRICE);
            }}
            sx={priceOptionSx}
            componentsProps={{ typography: { sx: { display: 'contents' } } }}
            label={
              <div className='add-product__switch-container'>
                <Typography variant='body2' sx={priceCaptionSx}>
                  {t('common__text__pricePerPiece')}
                </Typography>
                <Controller
                  name={PACKAGE_FORM.UNIT_PRICE}
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => checkDecimalsAfterDot(value, errorThreeDecimalsAfterDot, UNIT_PRICE_DECIMALS),
                    min: { value: 0, message: t('errors__message__incorrectValue') },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      className={cx('add-product__price-field', {
                        'add-product__price-field_disabled': currentSwitch === PRICE_SWITCH_TYPE.PRICE,
                      })}
                      value={decimalInput.getDisplayValue({
                        fieldName: PACKAGE_FORM.UNIT_PRICE,
                        rawValue: field.value,
                        decimals: UNIT_PRICE_DECIMALS,
                      })}
                      onChange={(e) => {
                        decimalInput.startEditing(PACKAGE_FORM.UNIT_PRICE);
                        const value = e.target.value;
                        const valueWithDot = value.replace(',', '.');
                        field.onChange(valueWithDot);
                      }}
                      onBlur={() => {
                        field.onBlur();
                        decimalInput.stopEditing(PACKAGE_FORM.UNIT_PRICE);
                        handleChangePrice(PACKAGE_FORM.UNIT_PRICE);
                      }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      variant='outlined'
                      label={t('common__text__pricePerPiece')}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: ADORNMENTS.PRICE,
                      }}
                      inputProps={demoPlayback.getEditableInputProps(PACKAGE_FORM.UNIT_PRICE)}
                      sx={{
                        ...(shouldStyleUnitPriceInactive ? inactiveStyle : {})
                      }}
                    />
                  )}
                />
              </div>
            }
          />
          <FormControlLabel
            value={PRICE_SWITCH_TYPE.PRICE}
            control={
              <Radio
                checked={currentSwitch === PRICE_SWITCH_TYPE.PRICE}
                value={PRICE_SWITCH_TYPE.PRICE}
                disabled={demoPlayback.disabled}
              />
            }
            onClick={() => {
              if (isDemoMode) {
                return
              }

              setCurrentSwitch(PRICE_SWITCH_TYPE.PRICE);
            }}
            sx={priceOptionSx}
            componentsProps={{ typography: { sx: { display: 'contents' } } }}
            label={
              <div className='add-product__switch-container'>
                <Typography variant='body2' sx={priceCaptionSx}>
                  {t('common__text__total')}
                </Typography>
                <Controller
                  name={PACKAGE_FORM.PRICE}
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => checkDecimalsAfterDot(value, errorTwoDecimalsAfterDot, PRICE_DECIMALS),
                    min: { value: 0, message: t('errors__message__incorrectValue') },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      className={cx('add-product__price-field', {
                        'add-product__price-field_disabled': currentSwitch === PRICE_SWITCH_TYPE.UNIT_PRICE,
                      })}
                      value={decimalInput.getDisplayValue({
                        fieldName: PACKAGE_FORM.PRICE,
                        rawValue: field.value,
                        decimals: PRICE_DECIMALS,
                      })}
                      onChange={(e) => {
                        decimalInput.startEditing(PACKAGE_FORM.PRICE);
                        const value = e.target.value;
                        const valueWithDot = value.replace(',', '.');
                        field.onChange(valueWithDot);
                      }}
                      onBlur={() => {
                        field.onBlur();
                        decimalInput.stopEditing(PACKAGE_FORM.PRICE);
                        handleChangePrice(PACKAGE_FORM.PRICE);
                      }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      variant='outlined'
                      label={t('common__text__total')}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ endAdornment: ADORNMENTS.PRICE }}
                      disabled={demoPlayback.disabled}
                      sx={{
                        ...(shouldStyleTotalPriceInactive ? inactiveStyle : {})
                      }}
                    />
                  )}
                />
              </div>
            }
          />
        </RadioGroup>
      </FormControl>
    </div>
  </>
}

export default CommonFields
